import {use, useMemo} from 'react'
import {useQueryClient} from '@github-ui/react-query'

import {SessionContext} from '../contexts/SessionContext'
import {fetchAllEvents, generateEventsQueryKey, type EventsQueryData} from '../utils/fetch-session-events'
import type {Artifact} from '../utils/artifacts/artifact-core'
import {deriveArtifactsFromChatState} from '../utils/artifacts/derive-artifacts-from-chat-state'
import {deriveArtifactsFromSessionEvents} from '../utils/artifacts/derive-artifacts-from-session-events'
import {useAhpSessionEvents} from './use-ahp-session-events'
import {useCAPIQuery} from './useCAPIQuery'
import {useIsAhpSession} from './use-is-ahp-session'

/**
 * The files the active session's agent wrote, for the surrounding session view.
 *
 * A session's transcript arrives in one of two shapes and this picks the right
 * one: AHP-environment sessions replay an AHP `ChatState`, everything else
 * reads Mission Control's `SessionEvent` stream — the same split
 * {@link useSessionEvents} makes, which excludes AHP sessions from the REST
 * cache. Both queries are declared unconditionally and gated by `enabled`, so
 * only one is ever live.
 *
 * Both share their query key with the hook the timeline already uses, so
 * mounting this alongside a timeline re-reads that cache instead of issuing a
 * second fetch.
 */
export function useSessionArtifacts(): Artifact[] {
  const queryClient = useQueryClient()
  const sessionContext = use(SessionContext)
  const session = sessionContext?.session ?? null
  const {isAhpSession} = useIsAhpSession(session)
  const taskId = session?.id

  const {chatState} = useAhpSessionEvents({session, enabled: isAhpSession && !!taskId})

  // Deliberately not `useSessionEventsQuery`: that projects the full
  // conversation model, which is far more work than this needs. Sharing its
  // query key still shares the fetch.
  const restQuery = useCAPIQuery<EventsQueryData, Artifact[]>({
    queryKey: generateEventsQueryKey(taskId),
    queryFn: async () => {
      if (!taskId) throw new Error('No task ID provided')
      const queryKey = generateEventsQueryKey(taskId)
      const previous = queryClient.getQueryData<EventsQueryData>(queryKey)
      return fetchAllEvents(taskId, previous)
    },
    select: (data: EventsQueryData) => deriveArtifactsFromSessionEvents(data.events),
    enabled: !isAhpSession && !!taskId,
  })

  const ahpArtifacts = useMemo(() => deriveArtifactsFromChatState(chatState), [chatState])

  if (isAhpSession) return ahpArtifacts
  return restQuery.data ?? []
}
